import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import youtubeIcon from '../assets/youtube_icon.png';
import tiktokIcon from '../assets/tiktok_icon.png';
import instagramIcon from '../assets/insta_icon.png';
import {
  selectNotifications,
  selectUnreadCount,
  fetchNotifications,
  markAllRead,
} from '../features/notifications/notificationsSlice';
import { selectIsAuthenticated } from '../features/auth/authSlice';
import './NotificationBell.css';

// Facebook icon as inline SVG data URI (no PNG asset available)
const facebookIcon = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>')}`;

function NotificationBell() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const notifications = useSelector(selectNotifications);
  const unreadCount = useSelector(selectUnreadCount);
  const [isOpen, setIsOpen] = useState(false);
  const [shouldRenderDropdown, setShouldRenderDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch notifications on mount and every 5 minutes
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchNotifications());
    }
  }, [isAuthenticated, dispatch]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Keep dropdown mounted briefly so close animation can complete.
  useEffect(() => {
    if (isOpen) {
      setShouldRenderDropdown(true);
      return;
    }

    if (!shouldRenderDropdown) return;

    const timeoutId = setTimeout(() => {
      setShouldRenderDropdown(false);
    }, 180);

    return () => clearTimeout(timeoutId);
  }, [isOpen, shouldRenderDropdown]);

  const handleToggle = () => {
    const opening = !isOpen;
    setIsOpen(opening);
    if (opening && unreadCount > 0) {
      dispatch(markAllRead());
    }
  };

  const handleViewAll = () => {
    setIsOpen(false);
    navigate('/notifications');
  };

  if (!isAuthenticated) return null;

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getPlatformIcon = (platform) => {
    const platformLower = platform?.toLowerCase();
    if (platformLower === 'youtube') return youtubeIcon;
    if (platformLower === 'tiktok' || platformLower === 'tiktok_fyf') return tiktokIcon;
    if (platformLower === 'instagram') return instagramIcon;
    if (platformLower === 'facebook') return facebookIcon;
    return youtubeIcon;
  };

  const getGuidelinesPath = (platform) => {
    const platformLower = platform?.toLowerCase();
    if (platformLower === 'youtube') return '/guides/youtube-guidelines';
    else if (platformLower === 'tiktok') return '/guides/tiktok-guidelines';
    else if (platformLower === 'instagram') return '/guides/ai-content-guidelines';
    else if (platformLower === 'facebook') return '/guides/ai-content-guidelines';
    else if (platformLower === 'tiktok_fyf') return '/guides/tiktok-fyf-eligibility';
    else return '/articles';
  };

  const handleNotificationClick = (platform) => {
    setIsOpen(false);
    navigate(getGuidelinesPath(platform));
  };

  return (
    <div className="notification-bell-wrapper" ref={dropdownRef}>
      <button
        className="notification-bell-btn"
        onClick={handleToggle}
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="notification-badge" aria-hidden="true" />
        )}
      </button>
      {shouldRenderDropdown && (
        <div className={`notification-dropdown ${isOpen ? 'open' : 'closing'}`}>
          <div className="notification-dropdown-header">
            <h2>Notifications</h2>
            {notifications.length > 0 && (
              <button
                type="button"
                className="notification-view-all-btn"
                onClick={handleViewAll}
              >
                View all
              </button>
            )}
          </div>
          <div className="notification-dropdown-list">
            {notifications.length === 0 ? (
              <div className="notification-empty">
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.notification_id}
                  className={`notification-item ${!n.is_read ? 'unread' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleNotificationClick(n.platform)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleNotificationClick(n.platform);
                    }
                  }}
                >
                  <div className="notification-item-logo-column">
                    <img
                      src={getPlatformIcon(n.platform)}
                      alt={n.platform}
                      className="notification-item-logo"
                    />
                  </div>
                  <div className="notification-item-summary-column">
                    {n.change_summary}
                  </div>
                  <div className="notification-item-time-column">
                    {formatRelativeTime(n.created_on)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
